import { SpinnerComponent } from 'ui-library';
// Librairies
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit, signal, ViewChild, WritableSignal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatToolbarModule } from "@angular/material/toolbar";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { addYears, formatISO, parseISO } from "date-fns";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";


// Classes
import { Pop } from "src/app/classes/shared/pop";

// Components
import { ScheduleComponent } from "src/app/components/shared/schedule/schedule.component";

// Interfaces
import {
  defaultProject,
  serviceConfigs as defaultServiceConfigs,
  Project,
  Schedule,
  ServiceConfig,
} from "src/app/interfaces/api/project";

// Services
import { ProjectService } from "src/app/services/api/projects/project.service";
import { UserService } from "src/app/services/api/users/user.service";
import { AuthService } from "src/app/services/core/auth.service";

// Components
import { ButtonComponent, RangePickerComponent } from "ui-library";

const TEMPLATE_DB = "0378_template";

@Component({
  selector: "app-add-project",
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatToolbarModule,
    TranslateModule,
    SpinnerComponent,
    RangePickerComponent,
    NgxMaterialTimepickerModule,
    ButtonComponent,
    ScheduleComponent,
  ],
  standalone: true,
  templateUrl: "./add-project.component.html",
  styleUrl: "./add-project.component.scss",
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0, transform: "translateY(-20px)" })),
      transition("void <=> *", [animate("250ms ease-in-out")]),
    ]),
    trigger("slideDown", [
      transition(":enter", [
        style({ height: 0, opacity: 0, overflow: "hidden" }),
        animate("300ms ease-out", style({ height: "*", opacity: 1 })),
      ]),
      transition(":leave", [
        style({ overflow: "hidden" }),
        animate("250ms ease-in", style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class AddProjectComponent implements OnInit {
  // Services
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectService);
  private readonly pop = inject(Pop);

  // Components API

  // Signals
  readonly project: WritableSignal<Project> = signal({ ...defaultProject });

  // Variables
  private readonly destroyRef = inject(DestroyRef);
  user: any;
  loading = false;
  formType = false;
  timezones: string[] = Intl.supportedValuesOf("timeZone");
  schedule: any;
  selectedServices: string[] = [];
  projectType = "";
  types = ["antico", "lean", "safety", "kyd", "zone", "iot", "other"];
  serviceConfigs: Record<string, ServiceConfig> = { ...defaultServiceConfigs };
  availableServices: Array<{ value: string; icon: string; color: string; label: string }> = [];
  mergedUnitGroups: Array<{ key: string; units: string[] }> = [];
  unitQuantities: Record<string, number> = {};

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.user = this.authService.currentUserValue;
    this.projectType = "other";
    this.initSchedule();
    this.loadServiceConfigs();

    const now = formatISO(new Date());

    this.project.set({
      ...defaultProject,
      modality: "location",
      type: "other",
      createdAt: now,
      createdDate: now,
      startDate: now,
      endDate: formatISO(addYears(new Date(), 1)),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      services: { flowRealtime: "" },
      schedule: this.schedule,
      aiModules: { ...defaultProject.aiModules },
      demoData: [],
      nelia: { ...defaultProject.nelia },
    });

    this.buildAvailableServices();
  }

  // ─── Form helpers ─────────────────────────────────────────────────────────

  patchProject(patch: Partial<Project>): void {
    this.project.update(p => ({ ...p, ...patch }));
  }

  private buildAvailableServices(): void {
    this.availableServices = Object.entries(this.serviceConfigs).map(([key, config]) => ({
      value: key,
      icon: config.icon ?? "category",
      color: config.color ?? "#607d8b",
      label: config.name || key,
    }));
  }

  private loadServiceConfigs(): void {
    this.projectService.getProjects({ database: TEMPLATE_DB }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (projects) => {
        const serviceConfigs = projects[0]?.serviceConfigs;

        if (serviceConfigs && Object.keys(serviceConfigs).length > 0) {
          this.serviceConfigs = serviceConfigs;
          this.buildAvailableServices();
        }
      },
      error: (err) => console.error(err),
    });
  }

  // ─── Unit groups ──────────────────────────────────────────────────────────

  private extractGroupKey(name: string): string | null {
    return name.split(/[\s/_\-,]+/).find(t => /^[A-Z][A-Z0-9]*$/.test(t)) ?? null;
  }

  private buildUnitGroups(units: string[]): Array<{ key: string; units: string[] }> {
    const groupMap: Record<string, string[]> = {};
    for (const unit of units) {
      const key = this.extractGroupKey(unit) ?? unit;
      (groupMap[key] ??= []).push(unit);
    }
    return Object.entries(groupMap).map(([key, groupUnits]) => ({ key, units: groupUnits }));
  }

  private refreshUnitGroups(mergedUnits: string[]): void {
    this.mergedUnitGroups = this.buildUnitGroups(mergedUnits);
    const activeKeys = new Set(this.mergedUnitGroups.map(g => g.key));

    Object.keys(this.unitQuantities).forEach(k => {
      if (!activeKeys.has(k))
        delete this.unitQuantities[k];
    });

    this.mergedUnitGroups.forEach(g => {
      if (this.unitQuantities[g.key] === undefined)
        this.unitQuantities[g.key] = 1;
    });
  }

  // ─── Services ─────────────────────────────────────────────────────────────

  updateProjectServices(): void {
    if (!this.selectedServices) return;

    this.patchProject({ aiModules: { ...defaultProject.aiModules }, nelia: { ...defaultProject.nelia, enabled: false } });

    if (this.selectedServices.length === 0) {
      this.patchProject({ type: "other", demoData: [], datavizTabs: undefined, settingTabs: undefined });
      this.projectType = "other";
      this.mergedUnitGroups = [];
      this.unitQuantities = {};
      return;
    }

    const merged: ServiceConfig = { projectType: [], datavizTabs: [], settingTabs: [], predictions: [], data: [], riskEnabled: false };
    const configKeys = ["projectType", "datavizTabs", "settingTabs", "predictions", "data"] as const;

    this.selectedServices.forEach((service) => {
      const config = this.serviceConfigs[service];

      if (!config) return;

      configKeys.forEach((key) => {
        const a = merged[key], b = config[key];

        if (Array.isArray(a) && Array.isArray(b))
          (merged[key] as any[]) = [...new Set([...a, ...b])];
      });

      if (config.riskEnabled)
        merged.riskEnabled = true;
    });

    for (const type of this.types) {
      if (merged.projectType.includes(type)) {
        this.projectType = type;
        break;
      }
    }

    const aiModules = { ...defaultProject.aiModules };
    merged.predictions.forEach(p => { aiModules[p] = { enabled: true, iteration: "" }; });

    this.patchProject({
      type: this.projectType,
      datavizTabs: [...merged.datavizTabs],
      settingTabs: [...merged.settingTabs],
      demoData: [...merged.data],
      aiModules,
      nelia: { ...defaultProject.nelia, enabled: !!merged.riskEnabled },
    });

    this.refreshUnitGroups(merged.data.flatMap(d => d.units));
  }

  toggleService(value: string): void {
    const idx = this.selectedServices.indexOf(value);

    if (idx > -1)
      this.selectedServices.splice(idx, 1);
    else
      this.selectedServices.push(value);

    this.updateProjectServices();
  }

  isSelected(value: string): boolean {
    return this.selectedServices.includes(value);
  }

  // ─── Save ─────────────────────────────────────────────────────────────────

  saveProject(): void {
    const p = this.project();
    if (!p.name || !p.type) {
      this.pop.warning(this.translate.instant("form.error.missing_fields"));
      return;
    }

    this.patchProject({ author: this.user.id });

    if (p.demoData?.length && Object.keys(this.unitQuantities).length) {
      this.patchProject({
        demoData: p.demoData.map(d => {
          const expanded: Record<string, number> = {};

          for (const group of this.mergedUnitGroups) {
            const qty = this.unitQuantities[group.key] ?? 1;

            for (const unit of group.units) {
              expanded[unit] = qty;
            }
          }

          return { ...d, unitQuantities: expanded };
        })
      });
    }

    const snapshot = this.project();

    if (snapshot.id) {
      this.projectService.setProject(snapshot).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (res) {
            this.selectCurrentProject(snapshot.id);
            this.pop.toast(this.translate.instant("projects.project_updated"), "success", 4000, "save");
          }

          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.pop.error(this.translate.instant(err.error?.message ?? err.message));
        },
      });
    } else {
      this.projectService.addProject(snapshot).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (res) {
            this.selectCurrentProject(res.id);
            this.pop.toast(this.translate.instant("http.projects.add.success"), "success", 4000, "add_circle");
          }

          this.loading = false;
        },
        error: (e) => {
          this.loading = false;

          if (e.error?.trace === "duplicate")
            this.pop.toast(this.translate.instant("http.projects.add.duplicate"), "danger", 4000, "duplicate");
          else
            this.pop.error(this.translate.instant("http.projects.add.error", { errorCode: e.status }));
        },
      });
    }
  }

  selectCurrentProject(projectId: string): void {
    this.userService.setCurrentProject(projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (!res) return;
        this.projectService.getProjectById(projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project: any) => {
          this.projectService.setCurrentProject(project);
          setTimeout(() => this.router.navigate(["/project/project-settings/informations"]), 200);
        });
      },
      error: (err) => console.error(err),
    });
  }

  // ─── Misc ─────────────────────────────────────────────────────────────────

  onPickerChanged(data: { startDate: string; endDate: string }): void {
    this.patchProject({
      startDate: formatISO(parseISO(data.startDate)),
      endDate: formatISO(parseISO(data.endDate)),
    });
  }

  onScheduleChange(schedule: Schedule): void {
    this.schedule = schedule;
    this.patchProject({ schedule });
  }

  initSchedule(): void {
    this.schedule = {
      mon: { enable: true, times: [["00:00", "23:59"]] },
      tue: { enable: true, times: [["00:00", "23:59"]] },
      wed: { enable: true, times: [["00:00", "23:59"]] },
      thu: { enable: true, times: [["00:00", "23:59"]] },
      fri: { enable: true, times: [["00:00", "23:59"]] },
      sat: { enable: false, times: [["00:00", "23:59"]] },
      sun: { enable: false, times: [["00:00", "23:59"]] },
    };
  }

  cancel(): void {
    this.router.navigate(["projects"]);
  }

  toggleForm(isChecked: boolean): void {
    this.formType = isChecked;
  }
}
